import { useState, useMemo } from 'react'
import type { CostoEstimado, GastoProyecto, CategoriaCosto } from '@/lib/types'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import { IconPlus, IconAlertTriangle } from '@/components/icons/Icons'

interface Props {
  costosEstimados: CostoEstimado[]
  gastosProyecto: GastoProyecto[]
  moneda: string
  proyectoId: number
  onGastoAdded: () => void
}

const CATEGORIAS: { value: CategoriaCosto; label: string }[] = [
  { value: 'freelance', label: 'Freelance' },
  { value: 'herramienta', label: 'Herramienta' },
  { value: 'licencia', label: 'Licencia' },
  { value: 'infraestructura', label: 'Infraestructura' },
  { value: 'otro', label: 'Otro' },
]

function categLabel(cat: string): string {
  return CATEGORIAS.find(c => c.value === cat)?.label ?? cat
}

/**
 * Determine row color class based on deviation ratio:
 * - real <= estimado → ok (green)
 * - real > estimado * 1.1 → warn (yellow)
 * - real > estimado * 1.3 → danger (red)
 */
function deviationClass(estimado: number, real: number): string {
  if (real <= 0 || estimado <= 0) return ''
  if (real > estimado * 1.3) return 'expense-row-danger'
  if (real > estimado * 1.1) return 'expense-row-warn'
  return 'expense-row-ok'
}

export default function ExpenseTable({
  costosEstimados,
  gastosProyecto,
  moneda,
  proyectoId,
  onGastoAdded,
}: Props) {
  const [showModal, setShowModal] = useState(false)
  const [linkCostoId, setLinkCostoId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  // Form state
  const [formDesc, setFormDesc] = useState('')
  const [formMonto, setFormMonto] = useState('')
  const [formTipo, setFormTipo] = useState<CategoriaCosto>('otro')
  const [formComprobante, setFormComprobante] = useState('')
  const [formFecha, setFormFecha] = useState(() => new Date().toISOString().split('T')[0])

  // Map gastos to their linked costo_estimado_id
  const gastosByCostoId = useMemo(() => {
    const map = new Map<number, GastoProyecto[]>()
    for (const g of gastosProyecto) {
      if (g.costo_estimado_id) {
        const existing = map.get(g.costo_estimado_id) ?? []
        existing.push(g)
        map.set(g.costo_estimado_id, existing)
      }
    }
    return map
  }, [gastosProyecto])

  // Unlinked gastos (not associated with any costo estimado)
  const gastosExtra = useMemo(() => {
    return gastosProyecto.filter(g => !g.costo_estimado_id)
  }, [gastosProyecto])

  // Totals
  const totalEstimado = useMemo(
    () => r2(costosEstimados.reduce((s, c) => s + c.monto, 0)),
    [costosEstimados]
  )
  const totalReal = useMemo(
    () => r2(gastosProyecto.reduce((s, g) => s + g.monto, 0)),
    [gastosProyecto]
  )
  const totalDesviacion = r2(totalReal - totalEstimado)

  function openAddModal(costoEstimadoId: number | null) {
    setLinkCostoId(costoEstimadoId)
    // Pre-fill from linked costo if available
    if (costoEstimadoId) {
      const costo = costosEstimados.find(c => c.id === costoEstimadoId)
      if (costo) {
        setFormDesc(costo.descripcion)
        setFormMonto(String(costo.monto))
        setFormTipo(costo.categoria)
      }
    } else {
      setFormDesc('')
      setFormMonto('')
      setFormTipo('otro')
    }
    setFormComprobante('')
    setFormFecha(new Date().toISOString().split('T')[0])
    setShowModal(true)
  }

  async function handleSave() {
    if (!formDesc.trim() || !formMonto) {
      showToast('Completa descripcion y monto', 'error')
      return
    }
    setSaving(true)
    try {
      const record = {
        proyecto_id: proyectoId,
        costo_estimado_id: linkCostoId,
        descripcion: formDesc.trim(),
        monto: r2(Number(formMonto)),
        moneda,
        tipo: formTipo,
        comprobante_url: formComprobante.trim() || null,
        fecha: formFecha,
      }
      const { error } = await supabase.from('gastos_proyecto').insert(record as never)
      if (error) throw error
      showToast('Gasto registrado', 'success')
      setShowModal(false)
      onGastoAdded()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar gasto'
      showToast(message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const hasData = costosEstimados.length > 0 || gastosProyecto.length > 0

  if (!hasData) {
    return (
      <div className="expense-table-empty">
        No hay costos estimados ni gastos registrados para este proyecto.
        <button
          className="btn btn-sm btn-primary"
          onClick={() => openAddModal(null)}
          style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <IconPlus size={14} /> Registrar Gasto
        </button>
        {showModal && renderModal()}
      </div>
    )
  }

  function renderModal() {
    return (
      <div className="modal-overlay" onClick={() => setShowModal(false)}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <div className="modal-header">
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Registrar Gasto Real</h3>
            <button className="modal-close" onClick={() => setShowModal(false)} type="button">
              &times;
            </button>
          </div>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Descripcion</label>
              <input
                className="form-input"
                value={formDesc}
                onChange={e => setFormDesc(e.target.value)}
                placeholder="Descripcion del gasto"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Monto ({moneda})</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                min="0"
                value={formMonto}
                onChange={e => setFormMonto(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Categoria</label>
              <select
                className="form-input"
                value={formTipo}
                onChange={e => setFormTipo(e.target.value as CategoriaCosto)}
              >
                {CATEGORIAS.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Comprobante URL (opcional)</label>
              <input
                className="form-input"
                value={formComprobante}
                onChange={e => setFormComprobante(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input
                className="form-input"
                type="date"
                value={formFecha}
                onChange={e => setFormFecha(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowModal(false)} type="button">
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} type="button">
              {saving ? 'Guardando...' : 'Guardar Gasto'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="expense-table-header">
        <button
          className="btn btn-sm btn-primary"
          onClick={() => openAddModal(null)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <IconPlus size={14} /> Registrar Gasto
        </button>
      </div>

      <div className="table-container" style={{ marginTop: 0 }}>
        <table className="expense-table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th>Categoria</th>
              <th style={{ textAlign: 'right' }}>Estimado</th>
              <th style={{ textAlign: 'right' }}>Real</th>
              <th style={{ textAlign: 'right' }}>Desviacion</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {/* Linked rows: each estimado with its linked gastos */}
            {costosEstimados.map(costo => {
              const linkedGastos = gastosByCostoId.get(costo.id) ?? []
              const realTotal = r2(linkedGastos.reduce((s, g) => s + g.monto, 0))
              const desviacion = r2(realTotal - costo.monto)
              const desviacionPct =
                costo.monto > 0 ? Math.round((desviacion / costo.monto) * 100) : 0
              const rowClass = deviationClass(costo.monto, realTotal)

              return (
                <tr key={`est-${costo.id}`} className={rowClass}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{costo.descripcion}</span>
                  </td>
                  <td>
                    <span className="expense-categ-badge">{categLabel(costo.categoria)}</span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(costo.monto, moneda)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {realTotal > 0 ? formatCurrency(realTotal, moneda) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {realTotal > 0 ? (
                      <span
                        className={`expense-desviacion ${desviacion > 0 ? 'expense-desviacion--over' : 'expense-desviacion--under'}`}
                      >
                        {desviacion > 0 ? '+' : ''}
                        {formatCurrency(desviacion, moneda)} ({desviacionPct > 0 ? '+' : ''}
                        {desviacionPct}%)
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {linkedGastos.length === 0 && (
                      <button
                        className="btn-icon-sm"
                        onClick={() => openAddModal(costo.id)}
                        title="Registrar gasto real"
                        type="button"
                      >
                        <IconPlus size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}

            {/* Extra rows: gastos not linked to any estimate */}
            {gastosExtra.map(g => (
              <tr key={`extra-${g.id}`} className="expense-row-extra">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconAlertTriangle size={13} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{g.descripcion}</span>
                    <span className="expense-extra-badge">Extra</span>
                  </div>
                  {g.fecha && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-muted)',
                        display: 'block',
                        marginTop: 2,
                      }}
                    >
                      {formatDate(g.fecha)}
                    </span>
                  )}
                </td>
                <td>
                  <span className="expense-categ-badge">{categLabel(g.tipo)}</span>
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>—</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatCurrency(g.monto, moneda)}
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>—</td>
                <td></td>
              </tr>
            ))}
          </tbody>

          {/* Footer totals */}
          <tfoot>
            <tr className="expense-table-footer">
              <td colSpan={2} style={{ fontWeight: 700 }}>
                Total
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>
                {formatCurrency(totalEstimado, moneda)}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>
                {formatCurrency(totalReal, moneda)}
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>
                <span
                  className={`expense-desviacion ${totalDesviacion > 0 ? 'expense-desviacion--over' : totalDesviacion < 0 ? 'expense-desviacion--under' : ''}`}
                >
                  {totalDesviacion > 0 ? '+' : ''}
                  {formatCurrency(totalDesviacion, moneda)}
                </span>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showModal && renderModal()}
    </>
  )
}
